import { useTimezone } from '@app/core/hooks/useTimezone';
import { Station } from '@app/core/types/stations';
import { getAuditListingDefaultDateRange } from '@app/modules/audit/modules/station-audit/helpers/default-listing-date-range';
import { AuditMatch } from '@app/modules/audit/modules/station-audit/types';
import {
  AUDIT_MATCHES_CRUD_MAX_LIMIT,
  datetimeRangeCrudFilters,
} from '@app/modules/audit/utils/audit-matches-crud';
import { auditSpotAuditSpotMatchesList } from '@mi-front/api-v2';
import { ReportView } from '../../../../../../../../core/reports/components/ReportView';
import { SpotMatchesForm } from './SpotMatchesForm';
import { SpotMatchesList } from './SpotMatchesList';
import { filterSpotMatchesByScheduleMeta } from './filter-spot-matches';
import { SpotMatchesListing } from './types';

type Props = {
  station: Station;
};

/**
 * Station spot matches listing; fetches via api-v2 CRUD matches list.
 */
export function SpotMatchesView(p: Props) {
  const tz = useTimezone();

  return (
    <ReportView<SpotMatchesListing>
      runOnLoad
      queryParamsTransform={{
        customId: {
          parse: (input) => input,
          serialize: (input) => input.replace(/\s/g, ''),
        },
      }}
      defaultValues={{
        ...getAuditListingDefaultDateRange(tz),
        customId: '',
        order: '',
        version: '',
      }}
      download={false}
      reportFn={async (values) => {
        const customIds = values.customId
          ?.split(',')
          .map((value) => value.trim())
          .filter((value) => !!value);

        const res = await auditSpotAuditSpotMatchesList({
          station: p.station.id,
          filter: [
            ...datetimeRangeCrudFilters(values.from, values.to),
            ...(customIds?.map((customId) => `customId||$eq||${customId}`) ?? []),
          ],
          limit: AUDIT_MATCHES_CRUD_MAX_LIMIT,
        });

        const matches = filterSpotMatchesByScheduleMeta(
          [...res.data].sort((m1, m2) =>
            new Date(m1.datetime) >= new Date(m2.datetime) ? -1 : 1,
          ) as AuditMatch[],
          values,
        );
        const hasScheduleMetaFilter = Boolean(values.order?.trim() || values.version?.trim());

        return {
          results: matches,
          total: hasScheduleMetaFilter ? matches.length : res.total,
        };
      }}
      Form={SpotMatchesForm}
      Result={SpotMatchesList}
    />
  );
}
